import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/state/AuthProvider";
import { Wordmark } from "@/components/common/Wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Sparkles, ShieldCheck, Inbox, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

export default function Login() {
  const { login, register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");  // "login" | "register"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signupCode, setSignupCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isRegister = mode === "register";

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isRegister) {
        await register({ email, password, name, signup_code: signupCode || undefined });
        toast.success("Account created — welcome");
      } else {
        await login({ email, password });
        toast.success("Welcome back");
      }
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.message || (isRegister ? "Sign up failed" : "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative grid min-h-screen w-full grid-cols-1 overflow-hidden lg:grid-cols-[1.05fr_1fr]" data-testid="login-page">
      {/* LEFT — brand storyteller */}
      <aside className="relative hidden flex-col justify-between overflow-hidden border-r p-10 lg:flex" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}>
        <div
          className="pointer-events-none absolute inset-0 -z-0"
          aria-hidden
          style={{
            backgroundImage:
              "radial-gradient(900px 420px at 18% -10%, rgba(18, 147, 79,0.22), transparent 60%), radial-gradient(700px 360px at 72% -12%, rgba(143, 224, 74,0.16), transparent 55%), radial-gradient(520px 260px at 92% 10%, rgba(246,178,74,0.10), transparent 55%)",
          }}
        />
        <Wordmark />
        <div className="relative z-10 max-w-[460px]">
          <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Social Content Engine</p>
          <h1 className="mt-3 text-4xl font-semibold leading-[1.05] tracking-[-0.02em] text-1">
            Your private cockpit for <span className="text-[color:var(--accent-cyan)]">agentic content</span> production.
          </h1>
          <p className="mt-4 text-[15px] leading-[1.6] text-muted-foreground">
            Run agents, review drafts, schedule with confidence. One operator. One workflow. Built to compound.
          </p>

          <div className="mt-8 grid gap-3">
            {[
              { icon: Sparkles, title: "Agent runs with skill routing", desc: "Phase-aware skills decide what gets generated, when." },
              { icon: Inbox, title: "Ingest from Claude, Codex, n8n, MCP", desc: "Drafts arrive in your inbox — then become a calendar." },
              { icon: CalendarDays, title: "Human-approved publishing", desc: "Nothing ships without your green light." },
            ].map(({ icon: Icon, title, desc }, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl border p-3 inner-highlight" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
                <span className="flex h-8 w-8 items-center justify-center rounded-md border" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }}>
                  <Icon className="h-4 w-4 text-[color:var(--accent-cyan)]" />
                </span>
                <div>
                  <p className="text-sm font-medium text-1">{title}</p>
                  <p className="text-xs text-muted-foreground">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-2 text-[11px] text-muted-foreground">
          <ShieldCheck className="h-3 w-3 text-[color:var(--accent-green)]" />
          <span>Single-operator workspace · Local backend active</span>
        </div>
      </aside>

      {/* RIGHT — sign in */}
      <main className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-[420px]">
          <div className="lg:hidden mb-8">
            <Wordmark />
          </div>
          <h2 className="text-2xl font-semibold tracking-[-0.01em] text-1">
            {isRegister ? "Create your workspace" : "Sign in to your workspace"}
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {isRegister
              ? "Your own private workspace — brands, content, keys, and connections, isolated to your account."
              : "Sign in to your private workspace."}
          </p>

          <form className="mt-8 space-y-4" onSubmit={onSubmit} data-testid="login-form">
            {isRegister && (
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-xs text-muted-foreground">Name</Label>
                <Input
                  id="name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  data-testid="register-form-name"
                  className="h-11"
                  style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs text-muted-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="login-form-email"
                className="h-11"
                style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs text-muted-foreground">Password</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete={isRegister ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isRegister ? "At least 8 characters" : "••••••••"}
                data-testid="login-form-password"
                className="h-11"
                style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
              />
            </div>
            {isRegister && (
              <div className="space-y-1.5">
                <Label htmlFor="signup-code" className="text-xs text-muted-foreground">
                  Signup code <span className="text-muted-foreground/70">(if required)</span>
                </Label>
                <Input
                  id="signup-code"
                  type="text"
                  value={signupCode}
                  onChange={(e) => setSignupCode(e.target.value)}
                  placeholder="Leave blank unless you were given one"
                  data-testid="register-form-code"
                  className="h-11"
                  style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
                />
              </div>
            )}
            <Button
              type="submit"
              data-testid="login-form-submit-button"
              disabled={submitting}
              className="mt-2 h-11 w-full gap-1.5 font-medium"
              style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}
            >
              {submitting ? (isRegister ? "Creating…" : "Signing in…") : (isRegister ? "Create account" : "Enter workspace")}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground" data-testid="auth-mode-toggle">
            {isRegister ? "Already have an account?" : "Don't have an account?"}{" "}
            <button
              type="button"
              onClick={() => setMode(isRegister ? "login" : "register")}
              className="font-medium text-[color:var(--accent-cyan)] hover:underline"
            >
              {isRegister ? "Sign in" : "Create one"}
            </button>
          </p>
        </div>
      </main>
      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}
